import { useState } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { useMutation } from '@apollo/client'
import { useRouter } from 'expo-router'
import { bignumber } from 'mathjs'
import * as math from 'mathjs'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PAYMENT_METHODS, SellOrderStatus } from '@/constants/common'
import { SELLER_REQUEST_CANCEL_SELL_ORDER } from '@/constants/graphql/mutations'
import { formatLocalFiatAmount, formatMarketPrice, formatTokenAmount } from '@/config/marketPresentation'
import type { PaymentResult, SellQueryResult } from '@/types/common'
import { cn } from '@/utils/cn'
import { delay } from '@/utils/delay'
import { formatDate } from '@/utils/formatter'
import { getBignumber, toCurrency } from '@/utils/numbers'
import { Button, useModal, useToast } from '@/components/base'
import BottomActionPanel from '@/components/BottomActionPanel'
import DescriptionListItem from '@/components/DescriptionListItem'
import { ReceiptOff } from '@/components/icon'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import OrderCard from '@/components/OrderCard'
import CancelModal from '@/components/orders/CancelModal'
import { usePaymentDetailScreen } from '@/components/orders/PaymentDetailScreen'
import PaymentSectionDetail from '@/components/orders/PaymentSectionDetail'
import SectionHeader from '@/components/SectionHeader'

export default function ParentOrderScreen({
  data,
  refetch
}: {
  data: SellQueryResult['userGetSellOrderByInternalKey']
  refetch: () => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const loading = useLoadingIndicator()
  const router = useRouter()
  const { bottom } = useSafeAreaInsets()
  const { openModal, closeModal } = useModal()
  const [selectedTab, setSelectedTab] = useState('orderDetail')
  const [cancelSellOrder] = useMutation(SELLER_REQUEST_CANCEL_SELL_ORDER, {
    onCompleted: async (data: any) => {
      // console.log('completed', data)
      await delay(500)
      refetch()
      loading.stop()
    },
    onError: (error) => {
      console.log('error', error)
      refetch()
      loading.stop()
      // const { graphQLErrors } = error
      // const errorCode = graphQLErrors[0]?.extensions.code
      toast.show({ variant: 'error', description: t('auth:toast.error.invalidOperationHint') })
    }
  })

  const showPanel = data.status === 1
  let title = t('orders:listing')
  if (data.status === SellOrderStatus.completed) {
    title = t('orders:transactionCompleted')
  } else if (data.status === SellOrderStatus.requestCancel || data.status === SellOrderStatus.canceling) {
    title = t('orders:cancelling')
  } else if (data.status === SellOrderStatus.canceled || data.status === SellOrderStatus.forceCanceled) {
    title = t('orders:transactionCancelled')
  }
  const options = [
    { value: 'orderDetail', text: t('orders:orderDetails') },
    { value: 'pairedOrders', text: t('orders:matchedOrders') }
  ]

  function handleCancelSellOrder() {
    loading.start()
    cancelSellOrder({
      variables: {
        sellOrderId: data.id
      }
    })
  }

  return (
    <>
      <ScrollView
        className={'flex-1 bg-background-secondary'}
        contentContainerStyle={{ paddingBottom: bottom + (showPanel ? 72 : 0) }}
      >
        <View className={'bg-background-primary px-4 pb-3 pt-2'}>
          <Text className={'text-2xl font-semibold text-text-primary'}>{title}</Text>
        </View>
        <View className={'relative flex-row bg-background-primary'}>
          <View className={'absolute inset-x-0 bottom-0 h-px bg-border-10'} />
          {options.map(({ value, text }, index) => (
            <Pressable
              key={index}
              className={'relative items-center justify-center px-4 py-3'}
              onPress={() => setSelectedTab(value)}
            >
              <Text
                className={cn(
                  'text-sm font-medium',
                  selectedTab === value ? 'text-text-primary' : 'text-text-tertiary'
                )}
              >
                {text}
              </Text>
              {selectedTab === value && (
                <View className={'absolute inset-x-3 bottom-0 mx-auto h-0.5 rounded-t-full bg-background-brand'} />
              )}
            </Pressable>
          ))}
        </View>
        {selectedTab === 'orderDetail' ? (
          <>
            <ParentOrderDetailSection data={data} />
            <ParentOrderPaymentSection data={data} />
          </>
        ) : (
          <>
            <SectionHeader>{t('orders:transactions')}</SectionHeader>
            {data.pairBuyOrder.length === 0 ? (
              <View className={'items-center gap-4 bg-background-secondary py-4'}>
                <ReceiptOff className={'size-20 text-text-quaternary'} />
                <Text className={'text-sm text-text-secondary'}>{t('orders:noOrders')}</Text>
              </View>
            ) : (
              <View className={'gap-4 px-4 pb-4'}>
                {data.pairBuyOrder.map(
                  ({ internalKey: childInternalKey, status, dealAmount, basePrice, pairedAt, createdAt }) => (
                    <OrderCard
                      key={childInternalKey}
                      orderType={'sell'}
                      orderStatus={status}
                      dealAmount={bignumber(dealAmount.value)}
                      basePrice={Number(basePrice)}
                      createdAt={pairedAt ?? createdAt}
                      onPress={() => {
                        router.push({
                          pathname: '/order/sell/[id]',
                          params: {
                            id: data.internalKey,
                            childId: childInternalKey
                          }
                        })
                      }}
                    />
                  )
                )}
              </View>
            )}
          </>
        )}
      </ScrollView>
      {showPanel && (
        <BottomActionPanel>
          <Button
            size={'md'}
            intent={'primary'}
            variant={'outline'}
            className={'flex-1'}
            disabled={loading.isLoading}
            onPress={() => {
              openModal({
                children: <CancelModal onSubmit={handleCancelSellOrder} />,
                onClose: closeModal
              })
            }}
          >
            {t('orders:cancelListing')}
          </Button>
        </BottomActionPanel>
      )}
    </>
  )
}

function ParentOrderDetailSection({ data }: { data: SellQueryResult['userGetSellOrderByInternalKey'] }) {
  const { t } = useTranslation()
  const isProcessing =
    data.status !== SellOrderStatus.completed &&
    data.status !== SellOrderStatus.canceled &&
    data.status !== SellOrderStatus.forceCanceled

  const remainingAmount = toCurrency(
    math
      .subtract(
        math.subtract(
          getBignumber(Number(data.expectDealAmount.value)),
          getBignumber(Number(data.actualDealAmount.value))
        ),
        getBignumber(Number(data.processingDealAmount.value))
      )
      .toString()
  )

  return (
    <>
      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} hasBorderTop={true} canCopy={true}>
        {data.internalKey}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>{formatDate(data.createdAt)}</DescriptionListItem>
      {data.finishedAt !== null && (
        <DescriptionListItem label={t('orders:completedAt')}>{formatDate(data.finishedAt)}</DescriptionListItem>
      )}
      <DescriptionListItem label={t('orders:price')}>{formatMarketPrice(data.basePrice)}</DescriptionListItem>
      <DescriptionListItem label={t('orders:listedQuantity')}>
        {formatTokenAmount(data.expectDealAmount.value)}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:minimumTransaction')}>
        {formatTokenAmount(data.minDealAmount.value)}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:completedQuantity')}>
        {formatTokenAmount(data.actualDealAmount.value)}
      </DescriptionListItem>
      {isProcessing && (
        <DescriptionListItem label={t('orders:processingQuantity')}>
          {formatTokenAmount(data.processingDealAmount.value)}
        </DescriptionListItem>
      )}
      <DescriptionListItem label={t(isProcessing ? 'orders:remainingQuantity' : 'orders:refundedQuantity')}>
        {formatTokenAmount(remainingAmount)}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:receivedAmount')} isValuePrimary={true}>
        {formatLocalFiatAmount((Number(data.actualDealAmount.value) * Number(data.basePrice)).toString())}
      </DescriptionListItem>
      {isProcessing && (
        <DescriptionListItem label={t('orders:expectedTotal')} hasBorderBottom={true}>
          {formatLocalFiatAmount((Number(data.expectDealAmount.value) * Number(data.basePrice)).toString())}
        </DescriptionListItem>
      )}
    </>
  )
}

function ParentOrderPaymentSection({ data }: { data: SellQueryResult['userGetSellOrderByInternalKey'] }) {
  const { t } = useTranslation()
  const info = organize(data)
  const { open: openPaymentDetail, close: closePaymentDetail } = usePaymentDetailScreen()

  const startIndex = Object.values(info).findIndex((i) => i.length !== 0)
  const lastIndex = Object.values(info).findLastIndex((i) => i.length !== 0)
  return (
    <>
      <SectionHeader>{t('orders:paymentMethods')}</SectionHeader>
      {PAYMENT_METHODS.map((method, index) => {
        if (info[method].length === 0) {
          return null
        }
        return (
          <DescriptionListItem
            key={method}
            hasBorderTop={index === startIndex}
            hasBorderBottom={index === lastIndex}
            label={t(`payment:${method}`)}
            onPress={() => {
              openPaymentDetail({
                children: <PaymentSectionDetail data={info[method][0]} type={'sell'} />,
                onClose: closePaymentDetail,
                title: t('orders:paymentMethods')
              })
            }}
          >
            {''}
          </DescriptionListItem>
        )
      })}
    </>
  )
}

function organize(data: Record<string, any>): PaymentResult {
  return data.paymentMethod.reduce(
    (acc: PaymentResult, method: any) => {
      if (method.bank !== null) {
        acc.bank.push(method)
      } else if (method.alipay !== null) {
        acc.alipay.push(method)
      } else if (method.wechat !== null) {
        acc.wechat.push(method)
      } else if (method.ermb !== null) {
        acc.ermb.push(method)
      } else if (method.qq !== null) {
        acc.qq.push(method)
      }
      return acc
    },
    {
      bank: [],
      alipay: [],
      wechat: [],
      ermb: [],
      qq: []
    }
  )
}
